import { CacheStrategy } from '@rammetec/data-access';
import { AUDIT_FIELD_DESCRIPTORS, AUDIT_FOREIGN_KEYS } from './_auditFields.js';
import { BUSINESS_ALLOWED_DATA_SOURCES, BUSINESS_DEFAULT_DATA_SOURCE } from './dataSourcePolicy.js';
import { INVOICE_ITEM } from '../types/invoice-item.js';
import { FIELD_LIMITS } from '../validation/fieldLimits.js';
/**
 * Descriptor for the invoice line item (Invoice child entity).
 * Subcollection: org/{orgId}/invoice/{invoiceId}/item
 *
 * Resolve repository with context: { orgId, invoiceId }.
 * Owned child — no lifecycle; audit via AUDIT_* (request 0235).
 */
export const invoiceItemDescriptor = {
    name: INVOICE_ITEM,
    label: 'Item da nota fiscal',
    domain: 'fiscal',
    collectionPath: 'org/{orgId}/invoice/{invoiceId}/item',
    pathPatternKeys: {
        orgId: 'string',
        invoiceId: 'string',
    },
    version: 1,
    description: 'Invoice line item referencing a Product, with computed totalPrice',
    repositoryConfig: {
        dataSource: BUSINESS_DEFAULT_DATA_SOURCE,
        // NoCache: filho de entidade versionada (spec 0045) — itens em cache voltariam velhos após Recarregar e seriam regravados por cima.
        cache: CacheStrategy.NoCache,
    },
    allowedDataSources: BUSINESS_ALLOWED_DATA_SOURCES,
    fields: [
        { name: 'id', label: 'ID', type: 'string', required: true },
        {
            name: 'invoiceId',
            label: 'Nota fiscal',
            type: 'string',
            required: true,
            description: 'Owning invoice id (also encoded in the subcollection path)',
        },
        {
            name: 'lineNumber',
            example: 1,
            label: 'Item',
            type: 'number',
            required: true,
            description: '1-based line number',
        },
        {
            name: 'productId',
            label: 'Produto',
            type: 'string',
            required: true,
            description: 'ProductRef.productId',
        },
        {
            name: 'variantId',
            label: 'Variante',
            type: 'string',
            required: false,
            description: 'Partial ProductVariant (ProductRef.variantId)',
        },
        {
            name: 'description',
            example: 'Malha PV 30/1 Penteada - Vermelho Carmim',
            label: 'Descrição',
            type: 'string',
            required: true,
            maxLength: FIELD_LIMITS.NAME,
        },
        {
            name: 'unit',
            example: 'KG',
            label: 'Unidade',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.UNIT,
        },
        { name: 'quantity', example: 250, label: 'Quantidade', type: 'number', display: 'quantity', required: true },
        { name: 'unitPrice', example: 38.9, label: 'Preço unitário', type: 'number', display: 'money', required: true },
        { name: 'baseQuantity', example: 250, label: 'Qtd base', type: 'number', display: 'quantity', required: false },
        {
            name: 'baseUnit',
            example: 'KG',
            label: 'Unidade base',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.UNIT,
        },
        { name: 'conversionFactor', example: 1, label: 'Fator conversão', type: 'number', required: false },
        {
            name: 'totalPrice',
            example: 9725,
            label: 'Total',
            type: 'number',
            display: 'money',
            required: false,
            description: 'Computed = quantity * unitPrice',
        },
        { name: 'discountAmount', example: 0, label: 'Desconto', type: 'number', display: 'money', required: false },
        {
            name: 'cfop',
            example: '5101',
            label: 'CFOP',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        { name: 'icmsRate', example: 12, label: 'Alíq. ICMS', type: 'number', required: false },
        { name: 'icmsAmount', example: 1167, label: 'ICMS', type: 'number', display: 'money', required: false },
        { name: 'icmsStAmount', example: 0, label: 'ICMS ST', type: 'number', display: 'money', required: false },
        { name: 'ipiRate', example: 0, label: 'Alíq. IPI', type: 'number', required: false },
        { name: 'ipiAmount', example: 0, label: 'IPI', type: 'number', display: 'money', required: false },
        { name: 'pisAmount', example: 160.46, label: 'PIS', type: 'number', display: 'money', required: false },
        { name: 'cofinsAmount', example: 739.1, label: 'COFINS', type: 'number', display: 'money', required: false },
        { name: 'costAmount', example: 5625, label: 'Custo', type: 'number', display: 'money', required: false },
        {
            name: 'warehouseId',
            example: 'EXP',
            label: 'Depósito',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'purchaseOrderRef',
            example: '7712',
            label: 'OC',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'purchaseOrderItemRef',
            example: '1',
            label: 'Item OC',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        {
            name: 'lot',
            example: 'L-2026-10-031',
            label: 'Lote',
            type: 'string',
            required: false,
            maxLength: FIELD_LIMITS.CODE,
        },
        { name: 'expiryDate', example: '2027-10-22', label: 'Validade', type: 'date', required: false },
        {
            name: 'width',
            example: 1.75,
            label: 'Largura',
            type: 'number',
            required: false,
            groups: ['textil'],
        },
        {
            name: 'height',
            example: 0,
            label: 'Altura',
            type: 'number',
            required: false,
            groups: ['textil'],
        },
        ...AUDIT_FIELD_DESCRIPTORS,
    ],
    foreignKeys: [
        {
            fieldName: 'productId',
            targetEntity: 'Product',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Produto vinculado ao item',
        },
        {
            fieldName: 'variantId',
            targetEntity: 'ProductVariant',
            relationship: 'many-to-one',
            displayField: 'description',
            description: 'Variante parcial (identificadores; sem tamanho)',
        },
        {
            fieldName: 'invoiceId',
            targetEntity: 'Invoice',
            relationship: 'many-to-one',
            displayField: 'number',
            description: 'Nota fiscal de origem',
        },
        ...AUDIT_FOREIGN_KEYS,
    ],
};
//# sourceMappingURL=invoice-item.descriptor.js.map